(() => {
  'use strict';

  const roots = document.querySelectorAll(
    '[data-f2w-iconic]'
  );

  if (!roots.length) {
    return;
  }

  const normalize = (value) => {
    return String(value || '')
      .toLocaleLowerCase('nl-NL')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  const languageCode = (root) => {
    const currentRoot = String(
      window.Shopify?.routes?.root ||
      ''
    ).replace(/^\/+|\/+$/g, '');

    const rootLocale = currentRoot
      .split('/')[0]
      .toLowerCase();

    const locale = String(
      root?.dataset?.language ||
      document.documentElement?.lang ||
      rootLocale ||
      window.Shopify?.locale ||
      'nl'
    )
      .toLowerCase()
      .split(/[-_]/)[0];

    const supported = new Set([
      'da',
      'de',
      'en',
      'es',
      'fi',
      'fr',
      'it',
      'ja',
      'ko',
      'nb',
      'nl',
      'pl',
      'pt',
      'sv',
      'zh'
    ]);

    return supported.has(locale)
      ? locale.toUpperCase()
      : 'NL';
  };

  const parseValue = (value) => {
    if (
      value === null ||
      value === undefined
    ) {
      return [];
    }

    if (Array.isArray(value)) {
      return value
        .flatMap(parseValue)
        .filter(Boolean);
    }

    const text = String(value).trim();

    if (!text) {
      return [];
    }

    if (
      (text.startsWith('[') && text.endsWith(']')) ||
      (text.startsWith('{') && text.endsWith('}'))
    ) {
      try {
        const parsed = JSON.parse(text);

        if (Array.isArray(parsed)) {
          return parsed
            .flatMap(parseValue)
            .filter(Boolean);
        }

        if (
          parsed &&
          typeof parsed === 'object'
        ) {
          return Object.values(parsed)
            .flatMap(parseValue)
            .filter(Boolean);
        }
      } catch (error) {
        // Continue with normal text parsing.
      }
    }

    return text
      .split(/[\s,|]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const idsFrom = (field) => {
    return parseValue(field?.value)
      .filter((value) => {
        return String(value).startsWith('gid://');
      });
  };

  const unique = (values) => {
    return [
      ...new Set(
        values
          .flatMap((value) => {
            return Array.isArray(value)
              ? value
              : [value];
          })
          .map((value) => {
            return String(value || '').trim();
          })
          .filter(Boolean)
      )
    ];
  };

  const uniqueSorted = (values) => {
    const result = new Map();

    values
      .flatMap((value) => {
        return Array.isArray(value)
          ? value
          : [value];
      })
      .map((value) => {
        return String(value || '').trim();
      })
      .filter(Boolean)
      .forEach((value) => {
        const key = normalize(value);

        if (!result.has(key)) {
          result.set(key, value);
        }
      });

    return [...result.values()].sort((a, b) => {
      return a.localeCompare(b, 'nl-NL');
    });
  };

  const shuffle = (items) => {
    const shuffled = [...items];

    for (
      let index = shuffled.length - 1;
      index > 0;
      index -= 1
    ) {
      const randomIndex = Math.floor(
        Math.random() * (index + 1)
      );

      [
        shuffled[index],
        shuffled[randomIndex]
      ] = [
        shuffled[randomIndex],
        shuffled[index]
      ];
    }

    return shuffled;
  };

  const referenceLabel = (reference) => {
    return String(
      reference?.name?.value ||
      reference?.handle ||
      ''
    ).trim();
  };

  const referenceValue = (reference) => {
    if (!reference) {
      return null;
    }

    const label = referenceLabel(reference);

    if (!label && !reference.id) {
      return null;
    }

    return {
      id: String(reference.id || ''),
      label
    };
  };

  const nativeUrl = (value) => {
    if (!value) {
      return '';
    }

    try {
      const url = new URL(
        String(value),
        window.location.origin
      );

      if (
        url.protocol !== 'http:' &&
        url.protocol !== 'https:'
      ) {
        return '';
      }

      const root =
        window.Shopify?.routes?.root || '/';

      const prefix =
        root === '/'
          ? ''
          : root.replace(/\/$/, '');

      let path = url.pathname;

      if (/^\/[a-z]{2}(\/|$)/i.test(path)) {
        path = path.replace(/^\/[a-z]{2}/i, '');
      }

      if (
        prefix &&
        path.startsWith(`${prefix}/`)
      ) {
        path = path.slice(prefix.length);
      }

      return `${prefix}${path}${url.search}${url.hash}`;
    } catch (error) {
      return '';
    }
  };

  const countryFlagElement = (
    code,
    label
  ) => {
    const flagCode = String(code || '')
      .trim()
      .toLowerCase();

    const isNation =
      flagCode === 'gb-sct' ||
      flagCode === 'gb-eng' ||
      flagCode === 'gb-wls';

    if (
      !flagCode ||
      (flagCode.length !== 2 && !isNation)
    ) {
      return null;
    }

    const wrapper =
      document.createElement('span');
    wrapper.className = 'f2w-country-flag';

    const image =
      document.createElement('img');
    image.src =
      `https://flagcdn.com/w40/${flagCode}.png`;
    image.srcset =
      `https://flagcdn.com/w80/${flagCode}.png 2x`;
    image.alt = String(label || '');
    image.width = 20;
    image.height = 15;
    image.loading = 'lazy';
    image.decoding = 'async';

    wrapper.appendChild(image);
    wrapper.appendChild(
      document.createTextNode('\u00a0')
    );

    return wrapper;
  };

  const element = (tag, className, text) => {
    const node = document.createElement(tag);

    if (className) {
      node.className = className;
    }

    if (
      text !== undefined &&
      text !== null
    ) {
      node.textContent = text;
    }

    return node;
  };

  const ICONIC_QUERY_FIELDS = `
    metaobjects(type: "venue", first: 250, after: $cursor) {
      pageInfo { hasNextPage endCursor }
      nodes {
        id
        handle
        onlineStoreUrl
        name: field(key: "name") { value }
        editorial_profile: field(key: "editorial_profile") { value }
        city: field(key: "city") { value }
        resolved_image_url: field(key: "resolved_image_url") { value }
        country: field(key: "country") {
          reference {
            ... on Metaobject {
              id
              handle
              name: field(key: "name") { value }
              code: field(key: "code") { value }
            }
          }
        }
        sports: field(key: "sports") {
          references(first: 250) {
            nodes {
              ... on Metaobject {
                id
                handle
                name: field(key: "name") { value }
              }
            }
            pageInfo { hasNextPage endCursor }
          }
        }
      }
    }
  `;

  const ICONIC_QUERY_CONTEXT = `
    query F2WIconic($cursor: String, $language: LanguageCode!)
      @inContext(language: $language) {
      ${ICONIC_QUERY_FIELDS}
    }
  `;

  const ICONIC_QUERY_FALLBACK = `
    query F2WIconic($cursor: String) {
      ${ICONIC_QUERY_FIELDS}
    }
  `;

  const ICONIC_CANONICAL_QUERY = `
    query F2WIconicCanonical($ids: [ID!]!)
      @inContext(language: EN) {
      nodes(ids: $ids) {
        ... on Metaobject {
          id
          editorial_profile: field(key: "editorial_profile") { value }
        }
      }
    }
  `;

  const ICONIC_NODES_QUERY = `
    query F2WIconicNodes(
      $ids: [ID!]!
      $language: LanguageCode!
    ) @inContext(language: $language) {
      nodes(ids: $ids) {
        ... on Metaobject {
          id
          handle
          onlineStoreUrl
          name: field(key: "name") { value }
          city: field(key: "city") { value }
          resolved_image_url: field(key: "resolved_image_url") { value }
          country: field(key: "country") {
            reference {
              ... on Metaobject {
                id
                handle
                name: field(key: "name") { value }
                code: field(key: "code") { value }
              }
            }
          }
          sports: field(key: "sports") {
            references(first: 250) {
              nodes {
                ... on Metaobject {
                  id
                  handle
                  name: field(key: "name") { value }
                }
              }
              pageInfo { hasNextPage endCursor }
            }
          }
        }
      }
    }
  `;

  roots.forEach((root) => {
    if (
      root.dataset.f2wInitialized === 'true'
    ) {
      return;
    }

    root.dataset.f2wInitialized = 'true';
    root.hidden = false;

    const token =
      root.dataset.storefrontToken || '';

    const shop =
      root.dataset.shopDomain ||
      window.Shopify?.shop ||
      window.location.hostname;

    const apiVersion =
      root.dataset.apiVersion ||
      '2024-10';

    const endpoint =
      root.dataset.storefrontEndpoint ||
      `https://${shop}/api/${apiVersion}/graphql.json`;

    const language = languageCode(root);

    const perPage = Math.max(
      1,
      parseInt(root.dataset.perPage, 10) || 48
    );

    const countFormatter = new Intl.NumberFormat(root.dataset.language || document.documentElement?.lang || 'nl');
    root.querySelectorAll('[data-f2w-iconic-snapshot-value]').forEach((value) => {
      const number = Number(value.textContent.trim());
      if (Number.isFinite(number)) value.textContent = countFormatter.format(number);
    });

    const searchInput = root.querySelector(
      '[data-f2w-iconic-filter-search]'
    );

    const countrySelect = root.querySelector(
      '[data-f2w-iconic-filter-country]'
    );
    const countryPicker = root.querySelector('[data-f2w-iconic-country-picker]');
    const countryQuick = root.querySelector('[data-f2w-iconic-country-quick]');
    const countryMore = root.querySelector('[data-f2w-iconic-country-more]');
    const countryPanel = root.querySelector('[data-f2w-iconic-country-panel]');
    const countryList = root.querySelector('[data-f2w-iconic-country-list]');
    let countryExpanded = false;

    const citySelect = root.querySelector(
      '[data-f2w-iconic-filter-city]'
    );

    const sportSelect = root.querySelector(
      '[data-f2w-iconic-filter-sport]'
    );

    const resetButton = root.querySelector(
      '[data-f2w-iconic-filter-reset]'
    );

    const resultCount = root.querySelector(
      '[data-f2w-iconic-results-count]'
    );

    const loadingElement = root.querySelector(
      '[data-f2w-iconic-loading]'
    );

    const errorElement = root.querySelector(
      '[data-f2w-iconic-error]'
    );

    const emptyElement = root.querySelector(
      '[data-f2w-iconic-empty]'
    );

    const resultsGrid = root.querySelector(
      '[data-f2w-iconic-results]'
    );

    const loadMoreButton = root.querySelector(
      '[data-f2w-iconic-load-more]'
    );

    const indexElement = root.querySelector(
      '[data-f2w-iconic-index]'
    );

    const indexUrl = root.dataset.indexUrl || '';

    const featuredSection = root.querySelector(
      '[data-f2w-iconic-editorial-featured]'
    );

    const featuredGrid = root.querySelector(
      '[data-f2w-iconic-featured-results]'
    );

    const labels = {
      results:
        root.dataset.labelResults || '',

      loading:
        root.dataset.labelLoading || '',

      error:
        root.dataset.labelError || '',

      empty:
        root.dataset.labelEmpty || '',

      loadMore:
        root.dataset.labelLoadMore || '',

      view:
        root.dataset.labelView || '',

      type:
        root.dataset.labelType || ''
    };

    const setElementVisibility = (node, visible) => {
      if (!node) return;
      node.hidden = !visible;
      node.style.setProperty(
        'display',
        visible ? '' : 'none',
        'important'
      );
      node.setAttribute(
        'aria-hidden',
        visible ? 'false' : 'true'
      );
    };

    const chunk = (items, size) => {
      const groups = [];
      for (let index = 0; index < items.length; index += size) {
        groups.push(items.slice(index, index + size));
      }
      return groups;
    };

    let locations = [];
    let visibleCount = perPage;
    let activeQuery = ICONIC_QUERY_CONTEXT;

    const request = async (
      query,
      variables
    ) => {
      if (!token) {
        throw new Error(
          'Missing Storefront token'
        );
      }

      const response = await fetch(
        endpoint,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token':
              token
          },
          body: JSON.stringify({
            query,
            variables
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          'Storefront request failed'
        );
      }

      const json = await response.json();

      if (
        Array.isArray(json.errors) &&
        json.errors.length
      ) {
        throw new Error(
          'Storefront query failed'
        );
      }

      return json;
    };

    const parsePublishedIndex = (payload) => {
      if (
        payload?.sharded === true &&
        Array.isArray(payload.parts)
      ) {
        const countries = {};
        payload.parts
          .slice()
          .sort((a, b) => (Number(a?.part) || 0) - (Number(b?.part) || 0))
          .forEach((part) => {
            if (part?.v !== 1 || typeof part.countries !== 'object' || part.countries === null) {
              throw new Error('Iconic index part is invalid');
            }
            Object.entries(part.countries).forEach(([countryId, bucket]) => {
              if (!countries[countryId]) {
                countries[countryId] = { c: String(bucket?.c || ''), r: [] };
              }
              if (Array.isArray(bucket?.r)) countries[countryId].r.push(...bucket.r);
            });
          });
        return parsePublishedIndex({ v: 1, count: payload.count, countries });
      }

      if (!payload || payload.v !== 1 || typeof payload.countries !== 'object' || payload.countries === null) {
        return null;
      }

      const records = [];
      Object.entries(payload.countries).forEach(([countryId, bucket]) => {
        const rows = Array.isArray(bucket?.r) ? bucket.r : null;
        if (!rows) throw new Error('Iconic index bucket is invalid');
        rows.forEach((row) => {
          if (!Array.isArray(row) || row.length !== 5 || typeof row[0] !== 'string' || typeof row[1] !== 'string' || typeof row[2] !== 'string' || typeof row[3] !== 'string' || !Array.isArray(row[4])) {
            throw new Error('Iconic index row is invalid');
          }
          records.push({ id: row[0], countryId, countryCode: String(bucket?.c || '').trim().toUpperCase() });
        });
      });

      if (typeof payload.count !== 'number' || payload.count !== records.length) {
        throw new Error('Iconic index count mismatch');
      }
      return records;
    };

    const readIndexPayload = async () => {
      const inlineValue = indexElement?.textContent?.trim();
      if (inlineValue) {
        try {
          return JSON.parse(inlineValue);
        } catch (error) {
          throw new Error('Published Iconic index is invalid');
        }
      }
      if (!indexUrl) return null;
      const response = await fetch(indexUrl);
      if (!response.ok) return null;
      return response.json();
    };

    const requestLocationPage = async (
      cursor
    ) => {
      const isContextQuery =
        activeQuery === ICONIC_QUERY_CONTEXT;

      const json = await request(
        activeQuery,
        isContextQuery
          ? {
              cursor: cursor || null,
              language
            }
          : {
              cursor: cursor || null
            }
      );

      if (!json.data?.metaobjects) {
        throw new Error(
          'No location data returned'
        );
      }

      return json.data.metaobjects;
    };

    const canonicalProfilesFor = async (nodes) => {
      const ids = nodes.map((node) => node.id).filter(Boolean);
      if (!ids.length) return new Map();

      const json = await request(
        ICONIC_CANONICAL_QUERY,
        { ids }
      );

      const profiles = new Map();
      (json.data?.nodes || []).forEach((node) => {
        if (node?.id) {
          profiles.set(
            node.id,
            normalize(node.editorial_profile?.value)
          );
        }
      });
      return profiles;
    };

    const mapLocation = (node, canonicalProfiles) => {
      const profile = canonicalProfiles.get(node.id) ||
        normalize(node.editorial_profile?.value);
      if (profile !== 'iconic') return null;

      const sports = node.sports?.references?.nodes
        ?.map(referenceValue)
        .filter(Boolean) || [];

      return {
        id: node.id || '',
        handle: node.handle || '',
        url: nativeUrl(node.onlineStoreUrl),
        name: String(node.name?.value || '').trim(),
        city: String(node.city?.value || '').trim(),
        country: node.country?.reference
          ? referenceValue(node.country.reference)
          : null,
        countryCode: String(
          node.country?.reference?.code?.value || ''
        ).trim(),
        sports,
        image: String(
          node.resolved_image_url?.value || ''
        ).trim()
      };
    };

    const loadPublishedIconic = async () => {
      const payload = await readIndexPayload();
      if (!payload) return false;

      const indexRecords = parsePublishedIndex(payload);
      if (!indexRecords) return false;

      const ids = indexRecords.map((record) => record.id);
      const pages = [];
      for (let index = 0; index < ids.length; index += 750) {
        const batches = chunk(ids.slice(index, index + 750), 250);
        const results = await Promise.all(
          batches.map((batch) => request(ICONIC_NODES_QUERY, {
            ids: batch,
            language
          }))
        );
        pages.push(...results);
      }

      const canonicalProfiles = new Map(
        ids.map((id) => [id, 'iconic'])
      );
      const mapped = pages
        .flatMap((page) => page.data?.nodes || [])
        .map((node) => mapLocation(node, canonicalProfiles))
        .filter((location) => location && location.name);
      const received = new Set(mapped.map((location) => location.id));

      if (received.size !== ids.length) {
        throw new Error('Published Iconic index hydration was incomplete');
      }

      locations = mapped.sort((a, b) => {
        return a.name.localeCompare(b.name, 'nl-NL');
      });
      return true;
    };

    const loadAllIconic = async () => {
      let fallbackUsed = false;

      while (true) {
        const all = [];
        const seenCursors = new Set();
        let cursor = null;

        try {
          do {
            if (cursor && seenCursors.has(cursor)) {
              throw new Error('Repeated Storefront cursor');
            }
            if (cursor) seenCursors.add(cursor);

            const page = await requestLocationPage(cursor);
            if (
              !Array.isArray(page?.nodes) ||
              typeof page?.pageInfo?.hasNextPage !== 'boolean' ||
              page.nodes.some((node) => {
                return !node ||
                  typeof node.id !== 'string' ||
                  !node.id.trim();
              }) ||
              (page.pageInfo.hasNextPage && (
                typeof page.pageInfo.endCursor !== 'string' ||
                !page.pageInfo.endCursor.trim()
              ))
            ) {
              throw new Error('Incomplete Storefront location page');
            }
            const nodes = page.nodes;
            const canonicalProfiles =
              await canonicalProfilesFor(nodes);

            all.push(
              nodes
                .map((node) => mapLocation(
                  node,
                  canonicalProfiles
                ))
                .filter((location) => {
                  return location && location.name;
                })
            );

            cursor = page.pageInfo?.hasNextPage
              ? page.pageInfo.endCursor
              : null;

            if (page.pageInfo?.hasNextPage && !cursor) {
              throw new Error('Missing next cursor');
            }
          } while (cursor);

          return all.flat().sort((a, b) => {
            return a.name.localeCompare(b.name, 'nl-NL');
          });
        } catch (requestError) {
          if (
            activeQuery === ICONIC_QUERY_CONTEXT &&
            !fallbackUsed
          ) {
            activeQuery = ICONIC_QUERY_FALLBACK;
            fallbackUsed = true;
            continue;
          }
          throw requestError;
        }
      }
    };

    const createCard = (location) => {
      const article = element(
        'article',
        'f2w-event-card'
      );

      article.dataset.f2wLocationCard = '';

      const card = location.url
        ? element(
            'a',
            'f2w-event-card__link'
          )
        : element(
            'div',
            'f2w-event-card__link'
          );

      if (location.url) {
        card.href = location.url;
      }

      const imageWrapper = element(
        'div',
        'f2w-event-card__image'
      );

      if (location.image) {
        const image = document.createElement(
          'img'
        );

        image.src = location.image;
        image.alt = location.name;
        image.width = 1100;
        image.height = 825;
        image.loading = 'lazy';

        imageWrapper.appendChild(
          image
        );
      } else {
        const placeholder = element(
          'div',
          'f2w-event-card__image--placeholder'
        );

        placeholder.appendChild(
          element(
            'span',
            '',
            location.name.slice(0, 1)
          )
        );

        imageWrapper.appendChild(
          placeholder
        );
      }

      const body = element(
        'div',
        'f2w-event-card__body'
      );

      const content = element(
        'div',
        'f2w-event-card__content'
      );

      if (labels.type) {
        const topline = element(
          'div',
          'f2w-event-card__topline'
        );

        topline.appendChild(
          element(
            'span',
            'f2w-event-card__period f2w-event-card__period--upcoming',
            labels.type
          )
        );

        content.appendChild(topline);
      }

      content.appendChild(
        element(
          'h2',
          'f2w-event-card__title',
          location.name
        )
      );

      if (location.city) {
        content.appendChild(
          element(
            'p',
            'f2w-event-card__location',
            location.city
          )
        );
      }

      const locationCountryLabel =
        location.country?.label || '';

      if (locationCountryLabel) {
        const countryElement = element(
          'p',
          'f2w-event-card__location',
          locationCountryLabel
        );

        const flagElement =
          countryFlagElement(
            location.countryCode,
            locationCountryLabel
          );

        if (flagElement) {
          countryElement.insertBefore(
            flagElement,
            countryElement.firstChild
          );
        }

        content.appendChild(
          countryElement
        );
      }

      if (
        location.url &&
        labels.view
      ) {
        content.appendChild(
          element(
            'span',
            'f2w-event-card__view',
            labels.view
          )
        );
      }

      body.appendChild(content);
      card.appendChild(imageWrapper);
      card.appendChild(body);
      article.appendChild(card);

      return article;
    };

    const renderFeatured = () => {
      if (
        !featuredSection ||
        !featuredGrid
      ) {
        return;
      }

      const candidates = locations.filter(
        (location) => location.image
      );

      if (!candidates.length) {
        featuredSection.hidden = true;
        featuredGrid.replaceChildren();
        return;
      }

      const featured = shuffle(
        candidates
      ).slice(0, 3);

      featuredGrid.replaceChildren();

      featured.forEach((location) => {
        featuredGrid.appendChild(
          createCard(location)
        );
      });

      featuredSection.hidden = false;
    };

    const currentFilters = () => {
      return {
        search:
          searchInput?.value || '',

        country:
          countrySelect?.value || '',

        city:
          citySelect?.value || '',

        sport:
          sportSelect?.value || ''
      };
    };

    const matchesReferenceFilter = (
      selected,
      reference
    ) => {
      if (!selected) {
        return true;
      }

      if (!reference) {
        return false;
      }

      return (
        selected === reference.id ||
        normalize(selected) ===
          normalize(reference.label)
      );
    };

    const filteredIconic = (ignoreSearch = false) => {
      const filters = currentFilters();

      const search = ignoreSearch ? '' : normalize(filters.search);

      const city = normalize(
        filters.city
      );

      return locations.filter((location) => {
        const searchText = normalize([
          location.name,
          location.city,
          location.country?.label || ''
        ].join(' '));

        const matchesSearch =
          !search ||
          searchText.includes(search);

        const matchesCountry =
          matchesReferenceFilter(
            filters.country,
            location.country
          );

        const matchesCity =
          !city ||
          normalize(location.city) === city;

        const matchesSport =
          !filters.sport ||
          location.sports.some((sport) => {
            return matchesReferenceFilter(
              filters.sport,
              sport
            );
          });

        return (
          matchesSearch &&
          matchesCountry &&
          matchesCity &&
          matchesSport
        );
      });
    };

    const updateUrl = () => {
      const url = new URL(
        window.location.href
      );

      const filters = currentFilters();

      const values = {
        q: filters.search.trim(),
        country: filters.country,
        city: filters.city,
        sport: filters.sport
      };

      Object.entries(values).forEach(
        ([key, value]) => {
          if (value) {
            url.searchParams.set(
              key,
              value
            );
          } else {
            url.searchParams.delete(key);
          }
        }
      );

      window.history.replaceState(
        window.history.state || {},
        '',
        url
      );
    };

    const renderArchive = () => {
      if (!resultsGrid) {
        return;
      }

      const filtered = filteredIconic();
      const activeQuery = Boolean(searchInput?.value.trim());
      const showSearch = filteredIconic(true).length > perPage || activeQuery;
      const searchWrap = root.querySelector('[data-f2w-iconic-search-wrap]');
      const cityWrap = root.querySelector('[data-f2w-iconic-city-wrap]');
      const filtersWrap = root.querySelector('[data-f2w-directory-filters]');
      if (searchWrap) searchWrap.hidden = !showSearch;
      if (cityWrap) cityWrap.hidden = !citySelect?.value;
      if (filtersWrap) filtersWrap.hidden = !showSearch && !citySelect?.value;

      const visible = filtered.slice(
        0,
        visibleCount
      );

      resultsGrid.replaceChildren();

      visible.forEach((location) => {
        resultsGrid.appendChild(
          createCard(location)
        );
      });

      if (resultCount) {
        resultCount.textContent =
          `${countFormatter.format(filtered.length)} ${labels.results}`.trim();
      }

      if (emptyElement) {
        setElementVisibility(
          emptyElement,
          filtered.length === 0
        );
      }

      if (loadMoreButton) {
        const hasMore =
          visible.length < filtered.length;

        setElementVisibility(
          loadMoreButton,
          hasMore
        );
        loadMoreButton.disabled = false;
        loadMoreButton.textContent =
          labels.loadMore;
      }

      updateUrl();
    };

    const rebuildSelect = (
      select,
      options,
      firstLabel
    ) => {
      if (!select) {
        return;
      }

      select.replaceChildren();

      const firstOption =
        document.createElement('option');

      firstOption.value = '';
      firstOption.textContent =
        firstLabel;

      select.appendChild(firstOption);

      options.forEach((item) => {
        const option =
          document.createElement('option');

        option.value = item.value;
        option.textContent = item.label;

        select.appendChild(option);
      });
    };

    const uniqueReferenceOptions = (
      references
    ) => {
      const result = new Map();

      references
        .filter(Boolean)
        .forEach((reference) => {
          const value =
            reference.id ||
            normalize(reference.label);

          if (
            value &&
            reference.label &&
            !result.has(value)
          ) {
            result.set(value, {
              value,
              label: reference.label
            });
          }
        });

      return [...result.values()].sort(
        (a, b) => {
          return a.label.localeCompare(
            b.label,
            'nl-NL'
          );
        }
      );
    };

    const uniqueCityOptions = (values) => {
      const result = new Map();

      values
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .forEach((value) => {
          const key = normalize(value);

          if (!result.has(key)) {
            result.set(key, {
              value: key,
              label: value
            });
          }
        });

      return [...result.values()].sort(
        (a, b) => {
          return a.label.localeCompare(
            b.label,
            'nl-NL'
          );
        }
      );
    };

    const buildFilters = () => {
      const countryLabel =
        countrySelect?.options?.[0]
          ?.textContent || '';

      const cityLabel =
        citySelect?.options?.[0]
          ?.textContent || '';

      const sportLabel =
        sportSelect?.options?.[0]
          ?.textContent || '';

      rebuildSelect(
        countrySelect,
        uniqueReferenceOptions(
          locations.map((location) => {
            return location.country;
          })
        ),
        countryLabel
      );

      rebuildSelect(
        citySelect,
        uniqueCityOptions(
          locations.map((location) => {
            return location.city;
          })
        ),
        cityLabel
      );

      rebuildSelect(
        sportSelect,
        uniqueReferenceOptions(
          locations.flatMap((location) => {
            return location.sports;
          })
        ),
        sportLabel
      );
    };

    const renderCountryChips = () => {
      if (!countryPicker || !countrySelect || !countryQuick || !countryMore || !countryPanel || !countryList) return;
      const options = [...countrySelect.options].slice(1);
      const counts = new Map();
      const codes = new Map();
      for (const location of locations) {
        const id = location.country?.id;
        if (!id) continue;
        counts.set(id, (counts.get(id) || 0) + 1);
        if (location.countryCode) codes.set(id, location.countryCode.toUpperCase());
      }
      const preference = ['NL', 'BE'];
      options.sort((a, b) => {
        const ai = preference.indexOf(codes.get(a.value)), bi = preference.indexOf(codes.get(b.value));
        if (ai !== bi && (ai >= 0 || bi >= 0)) return ai < 0 ? 1 : bi < 0 ? -1 : ai - bi;
        return (counts.get(b.value) || 0) - (counts.get(a.value) || 0) ||
          a.textContent.localeCompare(b.textContent, document.documentElement.lang || 'nl');
      });
      const featured = options.slice(0, 6);
      const selected = options.find((option) => option.value === countrySelect.value);
      if (selected && !featured.includes(selected)) { featured.pop(); featured.push(selected); }
      const makeChip = (option) => {
        const button = element('button', 'f2w-event-card__sport', option.textContent.trim());
        button.type = 'button';
        button.setAttribute('aria-pressed', String(countrySelect.value === option.value));
        const flag = option.value && countryFlagElement(codes.get(option.value), option.textContent.trim());
        if (flag) {
          flag.setAttribute('aria-hidden', 'true');
          const image = flag.querySelector('img');
          if (image) { image.alt = ''; image.addEventListener('error', () => flag.remove(), { once: true }); }
          button.prepend(flag);
        }
        button.addEventListener('click', () => {
          countrySelect.value = option.value;
          if (citySelect) citySelect.value = '';
          countryExpanded = !option.value;
          countrySelect.dispatchEvent(new Event('change', { bubbles: true }));
          countryQuick.querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true });
        });
        return button;
      };
      const visibleCountries = selected ? [selected] : featured;
      countryList.replaceChildren(...options.filter((option) => !visibleCountries.includes(option)).sort((a, b) => a.textContent.localeCompare(b.textContent, document.documentElement.lang || 'nl')).map(makeChip));
      const allChip = () => makeChip(countrySelect.options[0]);
      const showMore = !selected && options.length > 6;
      countryMore.hidden = !showMore || countryExpanded;
      countryMore.setAttribute('aria-expanded', String(showMore && countryExpanded));
      countryMore.textContent = root.dataset.moreCountries;
      countryQuick.replaceChildren(allChip(), ...visibleCountries.map(makeChip),
        ...(showMore && countryExpanded ? [countryPanel, countryMore] : [countryMore, countryPanel]));
      countryPanel.hidden = !showMore || !countryExpanded;
      countryPicker.hidden = false;
      root.dataset.f2wIconicChipsReady = 'true';
    };

    countryMore?.addEventListener('click', () => {
      countryExpanded = !countryExpanded;
      renderCountryChips();
      if (countryExpanded) countryList.querySelector('button')?.focus({ preventScroll: true });
      else countryMore.focus({ preventScroll: true });
    });
    countryPanel?.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      countryExpanded = false;
      renderCountryChips();
      countryMore.focus();
    });

    const rebuildCityFilter = () => {
      if (!citySelect) {
        return;
      }

      const selectedCountry =
        countrySelect?.value || '';
      const currentCity = citySelect.value;
      const cityLabel =
        citySelect.options?.[0]
          ?.textContent || '';

      const availableCities = uniqueCityOptions(
        locations
          .filter((location) => {
            return matchesReferenceFilter(
              selectedCountry,
              location.country
            );
          })
          .map((location) => location.city)
      );

      rebuildSelect(
        citySelect,
        availableCities,
        cityLabel
      );

      const cityStillAvailable = [
        ...citySelect.options
      ].some((option) => {
        return option.value === currentCity;
      });

      citySelect.value = cityStillAvailable
        ? currentCity
        : '';
    };

    const rebuildCountryFilter = () => {
      if (!countrySelect) {
        return;
      }

      const currentCountry =
        countrySelect.value;
      const selectedCity = normalize(
        citySelect?.value || ''
      );
      const countryLabel =
        countrySelect.options?.[0]
          ?.textContent || '';
      const availableCountries =
        uniqueReferenceOptions(
          locations
            .filter((location) => {
              return !selectedCity ||
                normalize(location.city) === selectedCity;
            })
            .map((location) => location.country)
        );

      rebuildSelect(
        countrySelect,
        availableCountries,
        countryLabel
      );

      const currentStillAvailable =
        availableCountries.some((option) => {
          return option.value === currentCountry;
        });

      if (currentStillAvailable) {
        countrySelect.value = currentCountry;
      } else if (
        selectedCity &&
        availableCountries.length === 1
      ) {
        countrySelect.value =
          availableCountries[0].value;
      } else {
        countrySelect.value = '';
      }
    };

    const selectValueFromUrl = (
      select,
      value
    ) => {
      if (!select || !value) {
        return '';
      }

      const exact = [...select.options].find(
        (option) => option.value === value
      );

      if (exact) {
        return exact.value;
      }

      const normalized = normalize(value);
      const byLabel = [...select.options].find(
        (option) => normalize(
          option.textContent
        ) === normalized
      );

      return byLabel?.value || '';
    };

    const applyUrlFilters = () => {
      const params =
        new URLSearchParams(
          window.location.search
        );

      if (searchInput) {
        searchInput.value =
          params.get('q') || '';
      }

      if (countrySelect) {
        countrySelect.value =
          selectValueFromUrl(
            countrySelect,
            params.get('country') || ''
          );
      }

      if (citySelect) {
        citySelect.value =
          selectValueFromUrl(
            citySelect,
            params.get('city') || ''
          );
      }

      if (sportSelect) {
        sportSelect.value =
          selectValueFromUrl(
            sportSelect,
            params.get('sport') || ''
          );
      }

      rebuildCityFilter();
      rebuildCountryFilter();
      rebuildCityFilter();
    };

    const resetFilters = () => {
      if (searchInput) {
        searchInput.value = '';
      }

      if (countrySelect) {
        countrySelect.value = '';
      }

      if (citySelect) {
        citySelect.value = '';
      }

      if (sportSelect) {
        sportSelect.value = '';
      }

      buildFilters();
      rebuildCityFilter();
      rebuildCountryFilter();
      rebuildCityFilter();
      countryExpanded = false;
      renderCountryChips();

      visibleCount = perPage;
      renderArchive();
    };

    const changeFilter = () => {
      visibleCount = perPage;
      renderArchive();
    };

    searchInput?.addEventListener(
      'input',
      changeFilter
    );

    countrySelect?.addEventListener(
      'change',
      () => {
        if (citySelect) citySelect.value = '';
        rebuildCityFilter();
        renderCountryChips();
        changeFilter();
      }
    );

    citySelect?.addEventListener(
      'change',
      () => {
        rebuildCountryFilter();
        rebuildCityFilter();
        renderCountryChips();
        changeFilter();
      }
    );

    sportSelect?.addEventListener(
      'change',
      changeFilter
    );

    resetButton?.addEventListener(
      'click',
      resetFilters
    );

    loadMoreButton?.addEventListener(
      'click',
      () => {
        visibleCount += perPage;
        renderArchive();
      }
    );

    const start = async () => {
      try {
        if (loadingElement) {
          setElementVisibility(
            loadingElement,
            true
          );
          loadingElement.textContent =
            labels.loading;
        }

        if (errorElement) {
          setElementVisibility(
            errorElement,
            false
          );
        }

        let usedPublishedIndex = false;

        try {
          usedPublishedIndex =
            await loadPublishedIconic();
        } catch (indexError) {
          usedPublishedIndex = false;
          locations = [];
        }

        if (!usedPublishedIndex) {
          locations = await loadAllIconic();
        }

        buildFilters();
        applyUrlFilters();
        renderCountryChips();
        renderFeatured();
        renderArchive();
      } catch (error) {
        if (resultsGrid) {
          resultsGrid.replaceChildren();
        }

        if (featuredSection) {
          featuredSection.hidden = true;
        }

        if (resultCount) {
          resultCount.textContent = '';
        }

        if (emptyElement) {
          setElementVisibility(
            emptyElement,
            false
          );
        }

        if (loadMoreButton) {
          setElementVisibility(
            loadMoreButton,
            false
          );
        }

        if (errorElement) {
          setElementVisibility(
            errorElement,
            true
          );
          errorElement.textContent =
            labels.error;
        }
      } finally {
        if (loadingElement) {
          setElementVisibility(
            loadingElement,
            false
          );
        }
      }
    };

    start();
  });
})();




